import React, { useEffect, useState } from 'react';
import { type AccessibilityRole, Platform, View } from 'react-native';

import { Box } from '@fhs-legacy/universal-components';
import { BlankScreen } from 'components/blank-screen';
import LoadingAnimation from 'components/loading-animation';
import { useAppSelector } from 'state/global-state';
import { selectSelectedOrder } from 'state/global-state/models/order/selected-order/selected-order.selectors';
import { useUIContext } from 'state/ui';

import { CartEntry } from './cart-entry';
import {
  Container,
  Divider,
  LoadingContainer,
  OrderDetailsScrollContainer,
} from './details-modal.styled';
import { ReceiptDetails } from './receipt-details';
import { RECEIPT_ORDER_DETAILS_TITLE_NATIVE_ID, ReceiptHeadInfo } from './receipt-head-info';

const ORDER_RESOLUTION_TIMEOUT_MS = 5000;

export function RestaurantReceipt() {
  const { formatCurrencyForLocale } = useUIContext();

  const order = useAppSelector(selectSelectedOrder);

  // The selected order is sourced from Redux (set when a recent order is
  // pressed). On direct entry (refresh, deep link, direct URL) it can be null
  // with nothing to fetch it, so bound the loading state with a timeout to
  // avoid an infinite spinner and fall back to the BlankScreen.
  const [orderResolutionTimedOut, setOrderResolutionTimedOut] = useState(false);

  useEffect(() => {
    if (order) {
      setOrderResolutionTimedOut(false);
      return;
    }

    const timeoutId = setTimeout(
      () => setOrderResolutionTimedOut(true),
      ORDER_RESOLUTION_TIMEOUT_MS
    );
    return () => clearTimeout(timeoutId);
  }, [order]);

  const orderLoading = !order && !orderResolutionTimedOut;

  return (
    <View style={{ flex: 1 }}>
      <Container style={{ flex: 1 }}>
        {!orderLoading && order ? (
          <OrderDetailsScrollContainer
            {...(Platform.OS === 'web'
              ? {
                  accessibilityRole: 'main' as AccessibilityRole,
                  accessibilityLabelledBy: RECEIPT_ORDER_DETAILS_TITLE_NATIVE_ID,
                }
              : {})}
          >
            <View>
              <ReceiptHeadInfo order={order} />
              <Divider />
            </View>
            <Box accessibilityRole={'list' as AccessibilityRole}>
              {order.cart.cartEntries.map((entry, i) => {
                if (entry.lineId === 'discount-offer') {
                  return null;
                }
                return (
                  <Box key={entry.name! + i} accessibilityRole={'listitem' as AccessibilityRole}>
                    <CartEntry
                      currencyFormatter={formatCurrencyForLocale}
                      entry={entry}
                      index={i}
                    />
                    <Divider accessibilityRole="none" importantForAccessibility="no" />
                  </Box>
                );
              })}
            </Box>
            <View>
              <ReceiptDetails order={order} />
            </View>
            {/* Creates padding from the bottom so the text doesnt get cut off */}
            <View style={{ height: 16 }} />
          </OrderDetailsScrollContainer>
        ) : orderLoading ? (
          <LoadingContainer>
            <LoadingAnimation />
          </LoadingContainer>
        ) : (
          <BlankScreen context={{ reason: 'loyalty transaction not found' }} />
        )}
      </Container>
    </View>
  );
}

export default RestaurantReceipt;
